import type { PropsWithChildren } from 'react';
import type { FlexDirectionProperty } from '@mentimeter/ragnar-styled';
import { alpha } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useTranslations } from 'next-intl';
import { useTheme } from '../data-mediation';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../utils/constants';
import { legacyThemeAnimatedBackgroundsMap } from '../misc/themes/map';

interface PresentationCanvasT {
  width?: number | string;
  height?: number | string;
  flexDirection?: FlexDirectionProperty | undefined;
  disableClickEvents?: boolean | undefined;
  ariaLabel?: string;
}

const C = ({
  backgroundUrl,
  width,
  height,
  flexDirection,
  disableClickEvents,
  ariaLabel,
  children,
}: PropsWithChildren<
  PresentationCanvasT & { backgroundUrl: string | undefined }
>) => {
  return (
    <Box
      as="main"
      aria-label={ariaLabel}
      width={width}
      height={height}
      overflow="hidden"
      alignItems="center"
      flexDirection={flexDirection}
      bg="bg"
      extend={({ theme }) => ({
        transition: `background ${theme.durations[2]}s ease`,
        backgroundImage: backgroundUrl
          ? `linear-gradient(
              ${alpha(theme.colors.bg, theme.backdropAlpha ?? 1)},
              ${alpha(theme.colors.bg, theme.backdropAlpha ?? 1)}
              ), url(${backgroundUrl})`
          : undefined,
        backgroundSize: 'cover',
        pointerEvents: disableClickEvents ? 'none' : 'auto',
      })}
      data-testid="presentation-canvas"
    >
      {children}
    </Box>
  );
};

export const PresentationCanvas = ({
  height = CANVAS_HEIGHT,
  width = CANVAS_WIDTH,
  flexDirection,
  disableClickEvents = false,
  renderBackground = true,
  children,
  slideImage,
  ariaLabel,
}: PropsWithChildren<
  PresentationCanvasT & {
    renderBackground?: boolean;
    slideImage: string | undefined;
  }
>) => {
  const { background_cropped_image_url: presentationThemeImage, config_id } =
    useTheme();
  const LegacyMovingBackground =
    config_id && legacyThemeAnimatedBackgroundsMap[config_id];
  const t = useTranslations('presenting.sfinx');

  return (
    <C
      width={width}
      height={height}
      backgroundUrl={slideImage || presentationThemeImage}
      flexDirection={flexDirection}
      disableClickEvents={disableClickEvents}
      ariaLabel={t('presentation_view_a11y')}
    >
      {LegacyMovingBackground !== undefined &&
        !slideImage &&
        renderBackground && (
          <>
            <LegacyMovingBackground />
            <LegacyMovingBackgroundColorOverlay />
          </>
        )}
      {children}
    </C>
  );
};

const LegacyMovingBackgroundColorOverlay = () => (
  <Box
    bg="bg"
    position="absolute"
    width="100%"
    height="100%"
    top={0}
    left={0}
    zIndex={0}
    extend={({ theme }) => {
      return {
        transition: `all ${theme.durations[2]}s ease`,
        opacity: theme.backdropAlpha,
      };
    }}
  />
);
